// src/components/AttachmentMenu.jsx
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { FiCamera, FiImage, FiFile, FiMessageSquare } from "react-icons/fi";
import { MdDocumentScanner } from "react-icons/md";
import CameraCaptureModal from "./CameraCaptureModal";

const MAX_FILE_SIZE_MB = 25;

// ✅ Keep MIME list aligned with AttachmentController.jsx
const ALLOWED_FILE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/jpg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
]);

// ✅ Keep extensions aligned with AttachmentController.jsx
function hasAllowedExtension(name = "") {
  return /\.(pdf|docx?|xlsx?|csv|txt|jpe?g|png|webp)$/i.test(name || "");
}

// Optional (menu-side) validation for fallback flows only
const isValidFile = (file) => {
  if (!file) return false;

  const sizeOk = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

  // Some browsers provide empty type; allow by extension
  const typeOk = ALLOWED_FILE_TYPES.has(file.type) || hasAllowedExtension(file.name);

  if (!sizeOk) alert("File too large. Max allowed is 25MB.");
  if (!typeOk) alert("Unsupported file type.");

  return sizeOk && typeOk;
};

// A single accept string used for the Files picker (best UX)
const FILES_ACCEPT =
  ".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv," +
  "image/*,application/pdf,application/msword," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "text/plain,text/csv";

const AttachmentMenu = ({
  open,
  onClose,
  fileInputRef, // ✅ MUST point to AttachmentController's hidden input
  onFileSelected, // ⚠️ legacy fallback only (should not be used when fileInputRef exists)
  anchorRef,
  onOpenChatTranscript, // ✅ ChatInterface-owned transcript action, passed through ChatInput
  sessionKind = "",
}) => {
  // Vision Assist keeps the shared plus-menu shell, but manual attachments are
  // intentionally disabled there. Prefer the explicit sessionKind prop; the
  // route fallback makes the restriction effective for existing Vision callers
  // before they are updated to pass sessionKind directly.
  const normalizedSessionKind = String(sessionKind || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  const visionAssistMode =
    normalizedSessionKind === "vision_assist" ||
    (() => {
      try {
        if (typeof window === "undefined") return false;
        const path = String(window.location?.pathname || "").toLowerCase();
        return (
          path === "/vision" ||
          path.startsWith("/vision/") ||
          path === "/vision-assist" ||
          path.startsWith("/vision-assist/")
        );
      } catch {
        return false;
      }
    })();

  if (!open) return null;

  const menuRef = useRef(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [hasUserMessages, setHasUserMessages] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // 🔒 Enforce close no matter what (covers file pickers & modals)
  const forceClose = () => {
    try {
      onClose?.();
    } catch {}
    setTimeout(() => {
      try {
        onClose?.();
      } catch {}
    }, 0);
  };

  // ✅ Prefer routing everything through AttachmentController's hidden input.
  // If fileInputRef is missing, fall back to legacy input creation + onFileSelected.
  const openPickerViaController = ({ accept, capture } = {}) => {
    // Defense in depth: Vision Assist must never reach file/photo/camera
    // attachment selection through this shared menu.
    if (visionAssistMode) {
      forceClose();
      return;
    }

    forceClose();

    const inputEl = fileInputRef?.current;
    if (inputEl) {
      try {
        // reset so selecting same file again triggers onChange
        inputEl.value = "";

        if (typeof accept === "string") inputEl.accept = accept;
        else inputEl.accept = "";

        if (capture) inputEl.setAttribute("capture", capture);
        else inputEl.removeAttribute("capture");

        inputEl.click();
        return;
      } catch (e) {
        console.error("[AttachmentMenu] Failed to use controller inputRef:", e);
      }
    }

    // ⚠️ Fallback (legacy): create temp input and bubble file to onFileSelected
    // This bypasses AttachmentController unless parent forwards it manually.
    try {
      const tmp = document.createElement("input");
      tmp.type = "file";
      tmp.accept = accept || "";
      if (capture) tmp.setAttribute("capture", capture);
      tmp.onchange = () => {
        const f = tmp.files?.[0];
        if (!f) return;
        if (!isValidFile(f)) return;
        onFileSelected?.(f);
      };
      tmp.click();
    } catch (e) {
      console.error("[AttachmentMenu] Fallback picker failed:", e);
      alert("Failed to open file picker.");
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const checkChatLog = () => {
      const chatLog = document.getElementById("chat-log");
      const hasContent = chatLog && chatLog.innerText.trim() !== "";
      setHasUserMessages(hasContent);
    };
    const handleUserMessage = () => setHasUserMessages(true);
    checkChatLog();
    window.addEventListener("userHasSentMessage", handleUserMessage);
    return () => window.removeEventListener("userHasSentMessage", handleUserMessage);
  }, []);

  useLayoutEffect(() => {
    if (anchorRef?.current && menuRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const menuWidth = 200;
      const safeGap = 12;

      let top = rect.top - menuRef.current.getBoundingClientRect().height - safeGap;
      if (top < 50) top = 50;

      let left = rect.left + rect.width / 2 - menuWidth / 2;
      if (left < 10) left = 10;
      if (left + menuWidth > window.innerWidth - 10) {
        left = window.innerWidth - menuWidth - 10;
      }

      setMenuPosition({ top, left });
    }
  }, [anchorRef, isMobile, isTablet]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        !(anchorRef?.current && anchorRef.current.contains(e.target))
      ) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, anchorRef]);

  const handleScan = () => {
    if (visionAssistMode) {
      forceClose();
      return;
    }

    console.log("[AttachmentMenu] 📷 Scan clicked");

    if (isMobile || isTablet) {
      // capture environment camera on supported devices
      openPickerViaController({ accept: "image/*", capture: "environment" });
    } else {
      alert("Scan is only available on mobile or tablet.");
      forceClose();
    }
  };

  const handleCamera = () => {
    if (visionAssistMode) {
      forceClose();
      return;
    }

    console.log("[AttachmentMenu] 📷 Camera clicked");

    // ✅ Preferred: route through controller input (ensures token + backend flow)
    if (isMobile || isTablet) {
      openPickerViaController({ accept: "image/*", capture: "environment" });
      return;
    }

    // Desktop: also route through controller input (file picker for images)
    // If you want webcam modal, we keep it only as fallback when fileInputRef is missing.
    if (fileInputRef?.current) {
      openPickerViaController({ accept: "image/*" });
      return;
    }

    // Fallback: desktop webcam modal (legacy)
    forceClose();
    if (navigator.mediaDevices?.getUserMedia) {
      setShowCameraModal(true);
    } else {
      alert("Camera not supported on this device.");
    }
  };

  const handlePhotos = () => {
    if (visionAssistMode) {
      forceClose();
      return;
    }

    console.log("[AttachmentMenu] 🖼️ Photos clicked");
    openPickerViaController({ accept: "image/*" });
  };

  const handleFiles = () => {
    if (visionAssistMode) {
      forceClose();
      return;
    }

    console.log("[AttachmentMenu] 📁 Files clicked");
    openPickerViaController({ accept: FILES_ACCEPT });
  };

  const handleChatTranscript = () => {
    console.log("[AttachmentMenu] 💬 Chat transcript clicked");

    forceClose();

    try {
      onOpenChatTranscript?.();
    } catch (e) {
      console.error("[AttachmentMenu] Failed to open chat transcript:", e);
    }
  };

  return (
    <>
      <div
        ref={menuRef}
        style={{
          ...menuContainerStyle,
          top: menuPosition.top,
          left: menuPosition.left,
          position: "fixed",
        }}
      >
        {!visionAssistMode && (
          <>
            {(isMobile || isTablet) && (
              <div style={menuItemStyle} onClick={handleScan}>
                <MdDocumentScanner size={20} />
                <span style={labelStyle}>Scan</span>
              </div>
            )}

            <div style={menuItemStyle} onClick={handleCamera}>
              <FiCamera size={20} />
              <span style={labelStyle}>Camera</span>
            </div>

            <div style={menuItemStyle} onClick={handlePhotos}>
              <FiImage size={20} />
              <span style={labelStyle}>Photos</span>
            </div>

            <div style={menuItemStyle} onClick={handleFiles}>
              <FiFile size={20} />
              <span style={labelStyle}>Files</span>
            </div>

            <div style={menuDividerStyle} />
          </>
        )}

        <div
          style={chatTranscriptItemStyle}
          onClick={handleChatTranscript}
          role="button"
          tabIndex={0}
          aria-label="Chat transcript"
          title="Preview, print, or download this chat transcript"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleChatTranscript();
            }
          }}
        >
          <FiMessageSquare size={20} />
          <span style={labelStyle}>Chat transcript</span>
        </div>
      </div>

      {!visionAssistMode && showCameraModal && (
        <CameraCaptureModal
          onClose={() => setShowCameraModal(false)}
          onCapture={(file) => {
            // ⚠️ This is legacy fallback (only happens if fileInputRef is missing).
            setShowCameraModal(false);
            if (!file) return;
            if (!isValidFile(file)) return;
            onFileSelected?.(file);
          }}
        />
      )}
    </>
  );
};

const menuContainerStyle = {
  backgroundColor: "#fff",
  border: "1px solid #ddd",
  borderRadius: 10,
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  padding: 8,
  zIndex: 9999,
  display: "flex",
  flexDirection: "column",
  width: 200,
};

const menuItemStyle = {
  display: "flex",
  alignItems: "center",
  padding: "10px 12px",
  borderRadius: 8,
  cursor: "pointer",
  backgroundColor: "#f2f2f2",
  marginBottom: 6,
};

const menuDividerStyle = {
  height: 1,
  backgroundColor: "#e5e7eb",
  margin: "2px 4px 8px",
};

const chatTranscriptItemStyle = {
  display: "flex",
  alignItems: "center",
  padding: "10px 12px",
  borderRadius: 8,
  cursor: "pointer",
  backgroundColor: "#f8fafc",
  color: "#111827",
  marginBottom: 0,
};

const labelStyle = { marginLeft: 10, fontSize: 14 };

export default AttachmentMenu;
